import type { EntityDetail } from '../api/client.js';

export function ManualEditPane({
  workingCopy,
  isDirty,
  baseRevisionHash,
  onPatch,
  onReset,
}: {
  workingCopy: EntityDetail | null;
  isDirty: boolean;
  baseRevisionHash: string;
  onPatch: (mutator: (draft: EntityDetail) => EntityDetail) => void;
  onReset: () => void;
}) {
  if (!workingCopy) {
    return <div className="text-sm text-slate-500">Select an entity to begin manual editing.</div>;
  }

  const steps = workingCopy.steps.filter(isRecord);
  const responses = workingCopy.responses.filter(isRecord);

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="editor-badge">{isDirty ? 'Unsaved changes' : 'No local changes'}</div>
        <button
          className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-cream transition hover:bg-primary/90"
          onClick={onReset}
          type="button"
        >
          Reset
        </button>
      </div>

      <label className="grid gap-2 text-sm font-semibold text-slate-800">
        Title
        <textarea
          className="min-h-[88px] rounded-2xl border border-primary/12 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition focus:border-primary/30 focus:ring-0"
          onChange={(event) => onPatch((draft) => ({ ...draft, title: event.target.value }))}
          value={workingCopy.title}
        />
      </label>

      <label className="grid gap-2 text-sm font-semibold text-slate-800">
        Summary
        <textarea
          className="min-h-[88px] rounded-2xl border border-primary/12 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition focus:border-primary/30 focus:ring-0"
          onChange={(event) => onPatch((draft) => ({ ...draft, summary: event.target.value }))}
          value={workingCopy.summary ?? ''}
        />
      </label>

      {steps.map((step, index) => (
        <label key={String(step.id ?? index)} className="grid gap-2 text-sm font-semibold text-slate-800">
          Step {String(step.id ?? index)} prompt
          <textarea
            className="min-h-[88px] rounded-2xl border border-primary/12 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition focus:border-primary/30 focus:ring-0"
            onChange={(event) =>
              onPatch((draft) => ({
                ...draft,
                steps: draft.steps.map((candidate, candidateIndex) =>
                  candidateIndex === index && isRecord(candidate)
                    ? { ...candidate, prompt: event.target.value }
                    : candidate,
                ),
              }))
            }
            value={String(step.prompt ?? '')}
          />
        </label>
      ))}

      {responses.map((response, index) => (
        <label key={String(response.id ?? index)} className="grid gap-2 text-sm font-semibold text-slate-800">
          Response {String(response.id ?? index)} value
          <textarea
            className="min-h-[88px] rounded-2xl border border-primary/12 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition focus:border-primary/30 focus:ring-0"
            onChange={(event) =>
              onPatch((draft) => ({
                ...draft,
                responses: draft.responses.map((candidate, candidateIndex) =>
                  candidateIndex === index && isRecord(candidate)
                    ? { ...candidate, value: event.target.value }
                    : candidate,
                ),
              }))
            }
            value={String(response.value ?? '')}
          />
        </label>
      ))}

      {responses.map((response, index) => (
        <div key={`response-meta-${String(response.id ?? index)}`} className="grid gap-4 md:grid-cols-2">
          <label className="grid gap-2 text-sm font-semibold text-slate-800">
            Response {String(response.id ?? index)} is_correct
            <select
              className="rounded-2xl border border-primary/12 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-primary/30 focus:ring-0"
              onChange={(event) =>
                onPatch((draft) => ({
                  ...draft,
                  responses: draft.responses.map((candidate, candidateIndex) =>
                    candidateIndex === index && isRecord(candidate)
                      ? {
                          ...candidate,
                          is_correct:
                            event.target.value === 'null'
                              ? null
                              : event.target.value === 'true',
                        }
                      : candidate,
                  ),
                }))
              }
              value={String(response.is_correct)}
            >
              <option value="null">null</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </label>

          <label className="grid gap-2 text-sm font-semibold text-slate-800">
            Response {String(response.id ?? index)} next_step_id
            <input
              className="rounded-2xl border border-primary/12 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-primary/30 focus:ring-0"
              onChange={(event) =>
                onPatch((draft) => ({
                  ...draft,
                  responses: draft.responses.map((candidate, candidateIndex) =>
                    candidateIndex === index && isRecord(candidate)
                      ? {
                          ...candidate,
                          next_step_id: event.target.value ? Number(event.target.value) : null,
                        }
                      : candidate,
                  ),
                }))
              }
              type="number"
              value={response.next_step_id ?? ''}
            />
          </label>
        </div>
      ))}

      <div className="grid gap-3">
        <div className="text-sm font-semibold text-slate-900">View JSON</div>
        <div className="font-mono text-xs text-slate-500">Base revision hash: {baseRevisionHash}</div>
        <pre className="m-0 overflow-auto rounded-3xl bg-cream p-4 text-xs leading-6 text-slate-700">
          {JSON.stringify(workingCopy, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object';
}
